import { forceSignOut, isFatalAuthError } from './forceSignOut';

/**
 * Global fetch interceptor that catches 401 responses from the Supabase
 * REST/auth/edge endpoints and triggers a hard sign-out. This guards against
 * queries/mutations that forget to wire up handleSessionError.
 *
 * 403 is intentionally NOT treated as a session error here — many edge
 * functions return 403 for business-rule rejections (geofence, RLS, etc.)
 * and those must not log the user out.
 */
export function installAuthInterceptor(supabaseUrl: string | undefined): void {
  if (!supabaseUrl || typeof window === 'undefined' || !window.fetch) return;

  const origFetch = window.fetch.bind(window);
  const host = (() => {
    try {
      return new URL(supabaseUrl).host;
    } catch {
      return '';
    }
  })();
  if (!host) return;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await origFetch(input, init);
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;

      if (response.status === 401 && url.includes(host)) {
        // Authentication-only paths — token rejected
        const isAuthOrData =
          url.includes('/auth/v1/') ||
          url.includes('/rest/v1/') ||
          url.includes('/functions/v1/');
        if (isAuthOrData) {
          // Inspect body for a "missing sub" / bad_jwt hint without blocking
          response
            .clone()
            .json()
            .then((body) => {
              if (isFatalAuthError(body) || response.status === 401) {
                void forceSignOut('token_invalid');
              }
            })
            .catch(() => {
              // No JSON body or already read — still treat 401 as fatal
              void forceSignOut('token_invalid');
            });
        }
      }
    } catch {
      /* never let interception break the request */
    }
    return response;
  };
}
